import React, { useContext, useState } from "react";
import { HOST } from "../../constants/HostConstants";
import { UserContext, useStepperContext } from "../../context/MainContext";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import "react-accessible-accordion/dist/fancy-example.css";
import { propertyPrefillFromListing } from "../../actions/propertyPrefill";
import { hidalgoTaxLookup } from "../../actions/taxLookup";
import InputField, {
  TextareaField,
  SelectField,
  CheckboxField,
} from "../InputField";
import {
  DIR_OPTIONS,
  CITIES,
  COUNTIES,
  BATHS_OPTIONS,
  VACANT_TYPES,
} from "../../constants/propertyOptions";

import {
  TikTokEmbed,
  InstagramEmbed,
  FacebookEmbed,
  YouTubeEmbed,
  XEmbed,
  PinterestEmbed,
  LinkedInEmbed,
} from "react-social-media-embed";

// Street type lists: common top choices first, then a larger set to alphabetize
const TOP_STREET_TYPES = [
  "Street",
  "Avenue",
  "Road",
  "Drive",
  "Lane",
  "Boulevard",
  "Court",
  "Circle",
  "Way",
  "Parkway",
  "Highway",
  "Trail",
  "Loop",
  "Place",
  "Terrace",
];

const ALL_STREET_TYPES = Array.from(
  new Set([
    ...TOP_STREET_TYPES,
    "Alley",
    "Arcade",
    "Avenue North",
    "Avenue South",
    "Bypass",
    "Center",
    "Circle North",
    "Circle South",
    "Crescent",
    "Crossing",
    "Expressway",
    "Freeway",
    "Garden",
    "Grove",
    "Harbor",
    "Hollow",
    "Junction",
    "Key",
    "Knoll",
    "Landing",
    "Meadow",
    "Mews",
    "Oval",
    "Pass",
    "Pike",
    "Point",
    "Quay",
    "Reach",
    "Row",
    "Ridge",
    "Run",
    "Shoal",
    "Shore",
    "Spring",
    "Square",
    "Station",
    "Stravenue",
    "Street North",
    "Street South",
    "Terrace North",
    "Turnpike",
    "View",
    "Vista",
    "Walk",
    "Wall",
    "Way North",
    "Way South",
    "West",
    "Yard",
  ])
);

// Temporarily disable tax prefill UI and functionality
const TAX_PREFILL_ENABLED = false;

const Property = () => {
  const { propertyData, setPropertyData } = useStepperContext();
  const { user } = useContext(UserContext);
  const [touched, setTouched] = useState(new Set());
  const [errors, setErrors] = useState({});
  const [showFinancingDropdown, setShowFinancingDropdown] = useState(false);
  const SHOW_FEATURED_VIDEO = false;
  const [prefillSearch, setPrefillSearch] = useState("");
  const [prefillResults, setPrefillResults] = useState([]);
  const [prefillSearching, setPrefillSearching] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [prefillError, setPrefillError] = useState("");
  const [prefillSearchAttempted, setPrefillSearchAttempted] = useState(false);
  const [showPrefillDropdown, setShowPrefillDropdown] = useState(false);
  const [taxLoading, setTaxLoading] = useState(false);
  const [autoError, setAutoError] = useState("");
  const [autoLocked, setAutoLocked] = useState(false);
  const [taxStatus, setTaxStatus] = useState("idle"); // idle, looking, found, filling, failed
  const [taxResult, setTaxResult] = useState(null);
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [showPrefillModal, setShowPrefillModal] = useState(false);
  const [prefillStatus, setPrefillStatus] = useState("idle"); // idle, loading, success, error

  const safePropertyData =
    propertyData && typeof propertyData === "object" ? propertyData : {};

  const computeMissingFields = () => {
    const pd = safePropertyData || {};
    const missing = new Set();

    if (pd["vacant_land"] === "Yes") {
      if (!pd["acreage"]) missing.add("acreage");
      if (!pd["vacant_land_type"]) missing.add("vacant_land_type");
      if (
        pd["type"] === "Residential" &&
        pd["vacant_land_type"] === "Commercial"
      ) {
        missing.add("vacant_land_type_invalid");
      }
      return missing;
    }

    // Non-vacant required fields
    const requireKeys = [
      "street_number",
      "street_name",
      "street_type",
      // city can be in city_select or city
      "zip",
      "county",
      "price",
      "beds_total",
      "baths_full",
      "acreage",
      "living_sqft",
      "living_areas",
      "est_year_built",
    ];

    requireKeys.forEach((k) => {
      if (!pd[k]) missing.add(k);
    });

    // City special handling
    if (!pd["city_select"] && !pd["city"]) missing.add("city");

    return missing;
  };

  const missingFields = computeMissingFields();
  const fieldClass = (base, key) =>
    `${base} ${missingFields.has(key) ? "border-red-500" : ""}`;

  const inputBase =
    "bg-slate-100 px-4 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:outline-none";

  const handleBlurField = (key) => {
    setTouched((prev) => new Set(prev).add(key));
    // set or clear error
    if (missingFields.has(key)) {
      setErrors((prev) => ({ ...prev, [key]: "This field is required" }));
      return;
    }

    // Additional validation for acreage: allow up to 6 integer digits and up to 4 fractional digits
    // Accept leading-dot values like `.15` as requested (limit 999999.9999)
    if (key === "acreage") {
      const val = (safePropertyData["acreage"] || "")
        .toString()
        .replace(/,/g, "");
      if (val) {
        const acreageRegex = /^(?:\d{1,6}(?:\.\d{0,4})?|\.\d{1,4})$/;
        if (!acreageRegex.test(val)) {
          setErrors((prev) => ({
            ...prev,
            acreage: "Enter a value up to 999999.9999 (or .15)",
          }));
          return;
        }
      }
    }

    setErrors((prev) => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const addCommas = (num) => {
    if (!num) return "";
    num = num.replace(/\D/g, "");
    return num.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    if (
      [
        "zip",
        "street_number",
        "beds_total",
        "acreage",
        "living_sqft",
        "living_areas",
        "est_year_built",
      ].includes(name)
    ) {
      let maxLen = 6;
      let useCommas = false;
      if (name === "zip") maxLen = 5;
      if (name === "street_number") maxLen = 6;
      if (name === "beds_total") maxLen = 2;
      if (name === "acreage") {
        // Accept digits, one decimal separator (dot or comma), and up to 4 fractional digits.
        // Be permissive during typing but normalize to use dot.
        let cleaned = value.replace(/,/g, ".");
        // remove any characters except digits and dot
        cleaned = cleaned.replace(/[^0-9.]/g, "");
        // keep only the first dot if user typed multiple
        const firstDot = cleaned.indexOf(".");
        if (firstDot !== -1) {
          const intPart = cleaned.slice(0, firstDot);
          const fracPart = cleaned
            .slice(firstDot + 1)
            .replace(/\./g, "")
            .slice(0, 4);
          processedValue = intPart
            ? `${intPart}.${fracPart}`
            : fracPart
            ? `.${fracPart}`
            : "";
        } else {
          processedValue = cleaned;
        }
      } else {
        if (name === "living_sqft") {
          maxLen = 6;
          useCommas = true;
        }
        if (name === "living_areas") maxLen = 2;
        if (name === "est_year_built") maxLen = 4;
        let digitsOnly = value.replace(/\D/g, "");
        digitsOnly = digitsOnly.slice(0, maxLen);
        processedValue = useCommas ? addCommas(digitsOnly) : digitsOnly;
      }
    }
    setPropertyData({ ...safePropertyData, [name]: processedValue });
  };
  const financingOptionsList = [
    "Cash",
    "Conventional",
    "FHA",
    "Owner Financing",
    "Other",
    "VA",
  ];

  const handleFinancingToggle = (option) => {
    const current = (safePropertyData.financing_options || "").toString();
    const items = current ? current.split(",").map((s) => s.trim()) : [];
    const idx = items.indexOf(option);
    if (idx === -1) {
      items.push(option);
    } else {
      items.splice(idx, 1);
    }
    setPropertyData({
      ...safePropertyData,
      financing_options: items.join(", "),
    });
  };

  const handleChangeCheck = (e) => {
    const { name, value } = e.target;

    if (safePropertyData[name] === "Yes") {
      setPropertyData({ ...safePropertyData, [name]: "No" });
    } else if (safePropertyData[name] === "No") {
      setPropertyData({ ...safePropertyData, [name]: "Yes" });
    } else if (name === "acreage") {
      setPropertyData({ ...safePropertyData, [name]: value });
    } else if (!value) {
      setPropertyData({ ...safePropertyData, [name]: "Yes" });
    }
    // Typically handles the data that requires text whether than checkboxes.
    else {
      setPropertyData({ ...safePropertyData, [name]: value });
    }
  };

  const applyPrefillData = (payload) => {
    if (!payload) {
return;
    }
// Get the property data - handle both direct and nested formats
    const incomingData = payload.propertyData || payload || {};
// Start fresh with incoming data
    const merged = {
      ...incomingData,
    };

    // Preserve current price if set
    if (safePropertyData.price) {
      merged.price = safePropertyData.price;
    }

    // Preserve current for/type if set
    if (safePropertyData["for"]) {
      merged["for"] = safePropertyData["for"];
    }
    if (safePropertyData["type"]) {
      merged["type"] = safePropertyData["type"];
    }
setPropertyData(merged);
  };

  const handlePrefillSearch = async (query) => {
    setPrefillSearch(query);
    setPrefillError("");

    if (!query || query.length < 1) {
      setPrefillResults([]);
      setPrefillSearchAttempted(false);
      setShowPrefillDropdown(false);
      return;
    }

    // Need user to be logged in
    if (!user?.user_id) {
      setPrefillError("Please log in to use this feature");
      return;
    }

    setPrefillSearching(true);
    setPrefillSearchAttempted(true);

    try {
      // Fetch current user's properties (any status)
      const response = await fetch(`${HOST}/api/property/propertyByUser`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ user_id: user.user_id }),
      });

      if (!response.ok) {
        setPrefillResults([]);
        return;
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        setPrefillResults([]);
        return;
      }

      const data = await response.json();
      // propertyByUser returns recordsets array, flatten it
      const allProperties = Array.isArray(data)
        ? Array.isArray(data[0])
          ? data[0]
          : data
        : [];

      if (!Array.isArray(allProperties)) {
        setPrefillResults([]);
        return;
      }

      const lowerQuery = query.toLowerCase();

      // Search by property_id (key #), address, city, or county
      const filtered = allProperties
        .filter((prop) => {
          const propId = (prop.property_id || "").toString().toLowerCase();
          const streetNum = (prop.street_number || "").toString().toLowerCase();
          const streetName = (prop.street_name || "").toLowerCase();
          const fullAddress = `${streetNum} ${streetName}`.trim().toLowerCase();
          const city = (prop.city || "").toLowerCase();
          const county = (prop.county || "").toLowerCase();

          return (
            propId.includes(lowerQuery) ||
            fullAddress.includes(lowerQuery) ||
            city.includes(lowerQuery) ||
            county.includes(lowerQuery)
          );
        })
        .slice(0, 10); // Limit to 10 results

      setPrefillResults(filtered);
      setShowPrefillDropdown(true);
    } catch (error) {
setPrefillResults([]);
    } finally {
      setPrefillSearching(false);
    }
  };

  const handlePrefillSelect = async (property) => {
    const displayAddress = `${property.street_number || ""} ${
      property.street_name || ""
    }`.trim();
    setPrefillSearch(
      `#${property.property_id} - ${displayAddress}, ${property.city || ""}`
    );
    setShowPrefillDropdown(false);
    setPrefillResults([]);
    setPrefillLoading(true);
    setPrefillError("");

    // Show the prefill modal
    setShowPrefillModal(true);
    setPrefillStatus("loading");

    try {
      const payload = await propertyPrefillFromListing(property.property_id);
      setPrefillStatus("success");
      applyPrefillData(payload);
      // Close modal after short delay on success
      setTimeout(() => {
        setShowPrefillModal(false);
        setPrefillStatus("idle");
      }, 1500);
    } catch (error) {
      setPrefillStatus("error");
      setPrefillError("Unable to prefill from that listing. Try another.");
    } finally {
      setPrefillLoading(false);
    }
  };

  const handleTaxLookup = async () => {
    if (!TAX_PREFILL_ENABLED) return;
    setTaxLoading(true);
    setAutoError("");
    setTaxStatus("looking");
    setShowTaxModal(true);
    try {
      const q =
        safePropertyData.parcel_num_geo_tax_id ||
        safePropertyData.property_tax_number;
      if (!q) throw new Error("Tax ID is empty");
      const { mapped, raw, source } = await hidalgoTaxLookup(q);
      setTaxResult({ mapped, raw, source });
      if (mapped && Object.keys(mapped).length > 0) {
        // helper to pick numeric value from possible keys
        const pickNumeric = (obj, keys) => {
          for (const k of keys) {
            if (!obj) continue;
            for (const kk of Object.keys(obj)) {
              if (kk.toLowerCase().includes(k) && obj[kk]) return obj[kk];
            }
          }
          return undefined;
        };

        const parseAddress = (addr) => {
          if (!addr || typeof addr !== "string") return {};
          const parts = addr.split(",").map((p) => p.trim());
          const street = parts[0] || "";
          const cityStateZip = parts[1] || "";
          const cityMatch = cityStateZip.match(/^([^\d,]+?)\s*(\d{5})?$/);
          const city = cityMatch
            ? cityMatch[1].trim()
            : cityStateZip.split(" ")[0] || "";
          const zip = cityMatch && cityMatch[2] ? cityMatch[2] : undefined;
          const m = street.match(/^(\d+)\s+(.*)$/);
          return {
            address: addr,
            street_number: m ? m[1] : undefined,
            street_name: m ? m[2] : undefined,
            city: city || undefined,
            zip: zip || undefined,
          };
        };
        setTaxStatus("found");
        setTaxStatus("filling");
        // map address components if available
        const addrMap = parseAddress(
          mapped.situs_address ||
            mapped.address ||
            mapped.Situs ||
            mapped.situs ||
            ""
        );

        // baths
        const baths = pickNumeric(mapped, ["bath", "baths"]);

        setPropertyData({
          ...safePropertyData,
          legal_description:
            mapped.legal_description || safePropertyData.legal_description,
          acreage: mapped.acreage || safePropertyData.acreage,
          est_year_built:
            mapped.est_year_built || safePropertyData.est_year_built,
          living_sqft: mapped.living_sqft || safePropertyData.living_sqft,
          parcel_num_geo_tax_id:
            mapped.parcel_num_geo_tax_id ||
            safePropertyData.parcel_num_geo_tax_id,
          address: addrMap.address || safePropertyData.address,
          street_number:
            addrMap.street_number || safePropertyData.street_number,
          street_name: addrMap.street_name || safePropertyData.street_name,
          city: addrMap.city || safePropertyData.city,
          zip: addrMap.zip || safePropertyData.zip,
          baths_full: baths || safePropertyData.baths_full,
        });
        setTaxStatus("filled");
        setAutoLocked(true);
        setAutoError("");
      } else {
        setTaxStatus("failed");
        setAutoError("No CAD results found for that Tax ID.");
      }
    } catch (err) {
      setTaxStatus("failed");
      setAutoError(err?.message || "Unable to fetch CAD details.");
    } finally {
      setTaxLoading(false);
    }
  };

  const handleManualOverride = () => setAutoLocked(false);

  const normalizeSocialUrl = (url) => {
    if (!url) return "";
    let trimmed = url.trim();
    if (!/^https?:\/\//i.test(trimmed)) {
      trimmed = `https://${trimmed}`;
    }
    return trimmed;
  };

  const getVideoEmbedData = (url) => {
    const normalized = normalizeSocialUrl(url);
    if (!normalized) return { provider: null, embedUrl: "" };

    const lower = normalized.toLowerCase();

    if (lower.includes("tiktok.com")) {
      return {
        provider: "tiktok",
        embedUrl: normalized,
      };
    }

    const instaMatch = normalized.match(
      /instagram\.com\/(p|reel|tv)\/([^/?#]+)/i
    );
    if (instaMatch) {
      return {
        provider: "instagram",
        embedUrl: normalized,
      };
    }
    if (lower.includes("instagram.com")) {
      return { provider: "instagram", embedUrl: normalized };
    }

    if (lower.includes("facebook.com") || lower.includes("fb.watch")) {
      return {
        provider: "facebook",
        embedUrl: normalized,
      };
    }

    if (lower.includes("linkedin.com")) {
      return { provider: "linkedin", embedUrl: normalized };
    }

    if (lower.includes("pinterest.com")) {
      return { provider: "pinterest", embedUrl: normalized };
    }

    if (lower.includes("x.com") || lower.includes("twitter.com")) {
      return { provider: "x", embedUrl: normalized };
    }

    if (
      lower.includes("youtube.com/watch") ||
      lower.includes("youtu.be") ||
      lower.includes("youtube.com/shorts")
    ) {
      return { provider: "youtube", embedUrl: normalized };
    }

    return { provider: "default", embedUrl: normalized };
  };

  const renderVideoEmbed = (url) => {
    if (!url) return null;
    const { provider, embedUrl } = getVideoEmbedData(url);
    if (!embedUrl) return null;

    return (
      <div className="flex justify-center py-4">
        {provider === "tiktok" && <TikTokEmbed url={embedUrl} />}
        {provider === "instagram" && <InstagramEmbed url={embedUrl} />}
        {provider === "facebook" && <FacebookEmbed url={embedUrl} />}
        {provider === "youtube" && <YouTubeEmbed url={embedUrl} />}
        {provider === "linkedin" && <LinkedInEmbed url={embedUrl} />}
        {provider === "pinterest" && <PinterestEmbed url={embedUrl} />}
        {provider === "x" && <XEmbed url={embedUrl} />}
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      <div className="bg-slate-100 border border-slate-200 rounded p-4 mb-4 flex flex-col gap-3">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col">
            <p className="font-bold text-sm">
              Prefill from an existing listing
            </p>
            <p className="text-xs text-gray-600">
              Search by property key #, address, city, or county to prefill
              common fields. Price is not overwritten.
            </p>
          </div>
          <div className="relative">
            <input
              type="text"
              className="w-full bg-white border border-slate-300 px-3 py-2 rounded"
              placeholder="Search by key #, address, city..."
              value={prefillSearch}
              onChange={(e) => handlePrefillSearch(e.target.value)}
              onFocus={() => prefillSearch && setShowPrefillDropdown(true)}
              disabled={prefillLoading}
            />
            {prefillSearching && (
              <div className="absolute right-3 top-2 text-slate-500">
                <div className="animate-spin">⚙️</div>
              </div>
            )}
            {showPrefillDropdown && prefillResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 bg-white border border-slate-300 border-t-0 rounded-b shadow-lg z-10 max-h-64 overflow-y-auto">
                {prefillResults.map((prop) => (
                  <div
                    key={prop.property_id}
                    className="px-3 py-2 cursor-pointer hover:bg-slate-100 border-b border-slate-100 last:border-b-0"
                    onClick={() => handlePrefillSelect(prop)}
                  >
                    <p className="font-semibold text-sm">
                      🔑 #{prop.property_id}
                    </p>
                    <p className="text-xs text-slate-600">
                      {prop.street_number} {prop.street_name}, {prop.city}
                    </p>
                    <p className="text-xs text-slate-400">
                      {prop.property_status || "Unknown"} • {prop.county || ""}
                    </p>
                  </div>
                ))}
              </div>
            )}
            {prefillSearchAttempted &&
              prefillResults.length === 0 &&
              !prefillSearching && (
                <>
                  <div className="absolute top-full left-0 right-0 bg-white border border-slate-300 border-t-0 rounded-b shadow-lg z-10 p-3">
                    <p className="text-sm text-slate-600">
                      No properties found
                    </p>
                  </div>
                </>
              )}
          </div>
        </div>
        {prefillError && <p className="text-red-600 text-xs">{prefillError}</p>}

        {TAX_PREFILL_ENABLED && (
          <>
            <div className="bg-slate-50 border border-slate-200 rounded p-4 mt-3">
              <div className="flex flex-col">
                <p className="font-bold text-sm">
                  Prefill from existing property (Property Tax ID)
                </p>
                <p className="text-xs text-gray-600">
                  Enter a property tax or parcel ID to prefill address, legal
                  description, and other available fields.
                </p>
              </div>
              <div className="flex gap-2 mt-2">
                <input
                  type="text"
                  className="flex-1 bg-white border border-slate-300 px-3 py-2 rounded"
                  placeholder="Enter parcel/tax ID"
                  name="property_tax_number"
                  value={propertyData["property_tax_number"] || ""}
                  onChange={handleChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleTaxLookup();
                  }}
                  disabled={taxLoading}
                />
                <button
                  type="button"
                  className="bg-black text-white px-4 py-2 rounded"
                  onClick={handleTaxLookup}
                  disabled={taxLoading}
                >
                  {taxLoading ? "Fetching CAD…" : "Fetch from CAD"}
                </button>
              </div>
              {autoError && (
                <p className="text-xs text-red-600 mt-2">{autoError}</p>
              )}
              {autoLocked && (
                <button
                  type="button"
                  className="text-xs text-blue-700 underline mt-2"
                  onClick={handleManualOverride}
                >
                  Allow manual edits for auto-filled fields
                </button>
              )}
            </div>
            {/* Tax lookup status modal */}
            {showTaxModal && (
              <div className="fixed inset-0 flex items-center justify-center z-50">
                <div
                  className="absolute inset-0 bg-black/40"
                  onClick={() => setShowTaxModal(false)}
                />
                <div className="bg-white rounded p-6 z-60 w-[90%] max-w-md">
                  <h3 className="font-bold mb-2">Property Lookup</h3>
                  <p className="mb-2">
                    {taxStatus === "looking" && "Looking up property..."}
                    {taxStatus === "found" && "Found results."}
                    {taxStatus === "filling" && "Filling in property fields..."}
                    {taxStatus === "filled" && "Fields updated."}
                    {taxStatus === "failed" && "Lookup failed."}
                  </p>
                  {taxResult?.mapped && (
                    <div className="text-xs text-slate-700 mb-2">
                      <p className="font-semibold">Mapped Fields:</p>
                      <ul className="list-disc pl-5">
                        {Object.entries(taxResult.mapped).map(([k, v]) => (
                          <li key={k}>
                            <b>{k}:</b> {String(v).slice(0, 120)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {taxResult?.raw && (
                    <details className="text-xs text-slate-500 mt-2">
                      <summary>Show raw response</summary>
                      <pre className="whitespace-pre-wrap max-h-40 overflow-auto mt-2">
                        {String(taxResult.raw).slice(0, 10000)}
                      </pre>
                    </details>
                  )}
                  <div className="flex justify-end mt-4">
                    <button
                      className="px-3 py-1 bg-slate-200 rounded"
                      onClick={() => setShowTaxModal(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <hr />
      {propertyData["for"] === "Sale" &&
        propertyData["type"] === "Residential" && (
          <div className="w-full mb-4">
            <p className="text-red-600 font-bold text-sm text-center">
              THIS INFORMATION IS REQUIRED TO ADD A PROPERTY
            </p>
          </div>
        )}
      <div className="w-full">
        <Accordion
          className="flex flex-col gap-6"
          allowMultipleExpanded={true}
          allowZeroExpanded={false}
          preExpanded={[0, 1]}
        >
          <AccordionItem uuid={0}>
            <AccordionItemHeading className="text-19">
              <AccordionItemButton>Property Information</AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel>
              <div className="flex flex-col gap-6">
                <div className="flex flex-wrap justify-center gap-6">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-slate-700">
                      Street Number <span className="text-red-600 ml-1">*</span>
                    </label>
                    <InputField
                      className={fieldClass(inputBase, "street_number")}
                      name="street_number"
                      type="text"
                      value={propertyData["street_number"] || ""}
                      onChange={handleChange}
                      onBlur={() => handleBlurField("street_number")}
                      required
                    />
                    {touched.has("street_number") && errors.street_number && (
                      <span className="text-sm text-red-600">
                        {errors.street_number}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <label>Prefix</label>
                    <SelectField
                      className={`${inputBase} p-[2px] w-[120px]`}
                      name="dir_prefix"
                      value={propertyData["dir_prefix"] || ""}
                      onChange={handleChange}
                    >
                      {DIR_OPTIONS.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </SelectField>
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-slate-700">
                      Street Name <span className="text-red-600 ml-1">*</span>
                    </label>
                    <InputField
                      className={fieldClass(inputBase, "street_name")}
                      name="street_name"
                      type="text"
                      value={propertyData["street_name"] || ""}
                      onChange={handleChange}
                      onBlur={() => handleBlurField("street_name")}
                      required
                    />
                    {touched.has("street_name") && errors.street_name && (
                      <span className="text-sm text-red-600">
                        {errors.street_name}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col">
                    <label>Suffix</label>
                    <SelectField
                      className={`${inputBase} p-[2px] w-[120px]`}
                      name="dir_suffix"
                      value={propertyData["dir_suffix"] || ""}
                      onChange={handleChange}
                    >
                      {DIR_OPTIONS.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </SelectField>
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-slate-700">
                      Street Type <span className="text-red-600 ml-1">*</span>
                    </label>
                    <SelectField
                      className={
                        fieldClass(inputBase, "street_type") + " p-[6px]"
                      }
                      name="street_type_select"
                      value={
                        propertyData["street_type_select"] ||
                        propertyData["street_type"] ||
                        ""
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "__other__") {
                          setPropertyData({
                            ...safePropertyData,
                            street_type: "",
                            street_type_select: "__other__",
                          });
                        } else {
                          setPropertyData({
                            ...safePropertyData,
                            street_type: v,
                            street_type_select: v,
                          });
                        }
                      }}
                      onBlur={() => handleBlurField("street_type")}
                      required
                    >
                      <option value=""></option>
                      {/* Top choices in preferred order */}
                      {TOP_STREET_TYPES.map((st) => (
                        <option key={st} value={st}>
                          {st}
                        </option>
                      ))}
                      <option value="" disabled>
                        ────────────────────
                      </option>
                      {/* Remaining types alphabetically, excluding the top ones */}
                      {ALL_STREET_TYPES.filter(
                        (s) => !TOP_STREET_TYPES.includes(s)
                      )
                        .sort((a, b) => a.localeCompare(b))
                        .map((st) => (
                          <option key={st} value={st}>
                            {st}
                          </option>
                        ))}
                      <option value="__other__">Other (enter manually)</option>
                    </SelectField>

                    {propertyData["street_type_select"] === "__other__" && (
                      <input
                        className={`${inputBase} mt-2`}
                        name="street_type"
                        type="text"
                        placeholder="Enter street type (e.g., 'Terrace')"
                        value={propertyData["street_type"] || ""}
                        onChange={handleChange}
                        onBlur={() => handleBlurField("street_type")}
                      />
                    )}

                    {touched.has("street_type") && errors.street_type && (
                      <span className="text-sm text-red-600">
                        {errors.street_type}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap justify-center gap-6 items-end">
                  <div className="flex flex-col">
                    <label>
                      City <span className="text-red-600 ml-1">*</span>
                    </label>
                    <SelectField
                      className={fieldClass(inputBase, "city")}
                      name="city_select"
                      value={
                        propertyData["city_select"] ||
                        propertyData["city"] ||
                        ""
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "__other__") {
                          setPropertyData({
                            ...safePropertyData,
                            city: "",
                            city_select: "__other__",
                          });
                        } else {
                          setPropertyData({
                            ...safePropertyData,
                            city: v,
                            city_select: v,
                          });
                        }
                      }}
                      onBlur={() => handleBlurField("city")}
                      required
                    >
                      <option value=""></option>
                      <option>Alamo</option>
                      <option>Donna</option>
                      <option>Edcouch / Elsa</option>
                      <option>Edinburg</option>
                      <option>Harlingen</option>
                      <option>McAllen</option>
                      <option>Mission</option>
                      <option>Palmhurst</option>
                      <option>Pharr</option>
                      <option>Rio Grande City</option>
                      <option>San Juan</option>
                      <option>Weslaco</option>
                      <option value="__other__">Other (enter manually)</option>
                    </SelectField>

                    {propertyData["city_select"] === "__other__" && (
                      <input
                        className={`${inputBase} mt-2`}
                        name="city"
                        type="text"
                        placeholder="Enter city"
                        value={propertyData["city"] || ""}
                        onChange={handleChange}
                        onBlur={() => handleBlurField("city")}
                      />
                    )}
                    {touched.has("city") && errors.city && (
                      <span className="text-sm text-red-600">
                        {errors.city}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <label>
                      Zip <span className="text-red-600 ml-1">*</span>
                    </label>
                    <InputField
                      className={fieldClass(inputBase, "zip")}
                      name="zip"
                      type="text"
                      value={propertyData["zip"] || ""}
                      onChange={handleChange}
                      onBlur={() => handleBlurField("zip")}
                      required
                    />
                    {touched.has("zip") && errors.zip && (
                      <span className="text-sm text-red-600">{errors.zip}</span>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <label>State</label>
                    <SelectField
                      className={inputBase}
                      name="state"
                      value={propertyData["state"] || "Texas"}
                      onChange={handleChange}
                    >
                      <option>Texas</option>
                    </SelectField>
                  </div>
                  <div className="flex flex-col">
                    <label>
                      County <span className="text-red-600 ml-1">*</span>
                    </label>
                    <SelectField
                      className={fieldClass(inputBase, "county")}
                      name="county"
                      value={propertyData["county"] || ""}
                      onChange={handleChange}
                      onBlur={() => handleBlurField("county")}
                      required
                    >
                      <option value=""></option>
                      <option>Hidalgo</option>
                      <option>Cameron</option>
                      <option>Willacy</option>
                      <option>Starr</option>
                    </SelectField>
                  </div>
                </div>
                <div className="flex flex-wrap justify-center gap-6">
                  <div className="flex flex-col">
                    <label>Unit Number</label>
                    <InputField
                      className={inputBase}
                      name="unit_number"
                      type="text"
                      value={propertyData["unit_number"] || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col">
                    <label>Subdivision</label>
                    <InputField
                      className={inputBase}
                      name="subdivision"
                      type="text"
                      value={propertyData["subdivision"] || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                {/* School fields - moved from prefill area */}
                {propertyData["type"] === "Residential" &&
                  (propertyData["for"] === "Sale" ||
                    propertyData["for"] === "Lease") && (
                    <div className="flex flex-wrap justify-center gap-6 mt-2">
                      <div className="flex flex-col">
                        <label>School District</label>
                        <InputField
                          className={inputBase}
                          name="school_district"
                          type="text"
                          value={propertyData["school_district"] || ""}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="flex flex-col">
                        <label>Elementary School</label>
                        <InputField
                          className={inputBase}
                          name="elementary_school"
                          type="text"
                          value={propertyData["elementary_school"] || ""}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="flex flex-col">
                        <label>Middle School</label>
                        <InputField
                          className={inputBase}
                          name="middle_school"
                          type="text"
                          value={propertyData["middle_school"] || ""}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="flex flex-col">
                        <label>High School</label>
                        <InputField
                          className={inputBase}
                          name="high_school"
                          type="text"
                          value={propertyData["high_school"] || ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  )}
                <div className="flex flex-wrap justify-center gap-6">
                  <div className="flex flex-row gap-4 w-full max-w-2xl">
                    <div className="flex flex-col flex-1">
                      <label className="flex gap-2 items-center">
                        {propertyData["for"] === "Lease"
                          ? "Price Per Month"
                          : "Price"}
                        <span className="text-red-600 ml-1">*</span>
                        <p className="text-xs italic">
                          do not include commas, or decimals use format: 430000
                        </p>
                      </label>
                      <InputField
                        className={fieldClass(inputBase, "price")}
                        name="price"
                        type="text"
                        value={propertyData["price"] || ""}
                        onChange={handleChange}
                        onBlur={() => handleBlurField("price")}
                        required
                      />
                      {touched.has("price") && errors.price && (
                        <span className="text-sm text-red-600">
                          {errors.price}
                        </span>
                      )}
                    </div>
                    {propertyData["for"] === "Sale" &&
                      (propertyData["type"] === "Residential" ||
                        propertyData["type"] === "Commercial") && (
                        <div className="flex flex-col flex-1">
                          <label>Financing Options</label>
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() =>
                                setShowFinancingDropdown((s) => !s)
                              }
                              className="px-3 py-2 bg-white border rounded-md text-sm flex items-center gap-2 w-full"
                            >
                              <span>Financing Options</span>
                              <span className="text-xs text-gray-500">
                                {(propertyData.financing_options || "")
                                  .split(",")
                                  .map((s) => s.trim())
                                  .filter(Boolean)
                                  .join(", ")}
                              </span>
                            </button>
                            {showFinancingDropdown && (
                              <div className="absolute z-20 mt-2 bg-white border rounded-md p-3 shadow-lg w-full">
                                {financingOptionsList.map((opt) => (
                                  <label
                                    key={opt}
                                    className="flex items-center gap-2 text-sm mb-2"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={(
                                        propertyData.financing_options || ""
                                      )
                                        .split(",")
                                        .map((s) => s.trim())
                                        .includes(opt)}
                                      onChange={() =>
                                        handleFinancingToggle(opt)
                                      }
                                    />
                                    {opt}
                                  </label>
                                ))}
                                <div className="text-xs text-gray-500 mt-1">
                                  Shown on the listing next to the price.
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                  {/* Security Deposit - side by side with Price for Lease */}
                  {propertyData["for"] === "Lease" &&
                    (propertyData["type"] === "Commercial" ||
                      propertyData["type"] === "Residential") && (
                      <div className="flex flex-col">
                        <label className="flex gap-2 items-center">
                          Security Deposit
                          <p className="text-xs italic">
                            do not include commas, or decimals
                          </p>
                        </label>
                        <InputField
                          className={fieldClass(inputBase, "security_deposit")}
                          name="security_deposit"
                          type="text"
                          value={propertyData["security_deposit"] || ""}
                          onChange={handleChange}
                        />
                      </div>
                    )}
                </div>
              </div>
            </AccordionItemPanel>
          </AccordionItem>
          <AccordionItem uuid={1}>
            <AccordionItemHeading className="text-19">
              <AccordionItemButton>Property Details</AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel>
              {!propertyData["vacant_land"] ||
              propertyData["vacant_land"] === "No" ? (
                <div className="flex flex-col gap-6">
                  <div className="flex flex-wrap justify-center gap-6">
                    <div className="flex flex-col">
                      <label>
                        Beds Total <span className="text-red-600 ml-1">*</span>
                      </label>
                      <InputField
                        className={fieldClass(inputBase, "beds_total")}
                        name="beds_total"
                        type="text"
                        value={propertyData["beds_total"] || ""}
                        onChange={handleChange}
                        onBlur={() => handleBlurField("beds_total")}
                        required
                      />
                      {touched.has("beds_total") && errors.beds_total && (
                        <span className="text-sm text-red-600">
                          {errors.beds_total}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <label>
                        Baths <span className="text-red-600 ml-1">*</span>
                      </label>
                      <SelectField
                        className={fieldClass(
                          `${inputBase} p-[2px] w-[120px]`,
                          "baths_full"
                        )}
                        name="baths_full"
                        value={propertyData["baths_full"] || ""}
                        onChange={handleChange}
                        onBlur={() => handleBlurField("baths_full")}
                        required
                      >
                        <option></option>
                        <option>1/2 Baths</option>
                        <option>1 Baths</option>
                        <option>1 1/2 Baths</option>
                        <option>2 Baths</option>
                        <option>2 1/2 Baths</option>
                        <option>3 Baths</option>
                        <option>3 1/2 Baths</option>
                        <option>4 Baths</option>
                        <option>4 1/2 Baths</option>
                        <option>5 Baths</option>
                        <option>5 1/2 Baths</option>
                        <option>6 Baths</option>
                        <option>6 1/2 Baths</option>
                        <option>7 Baths</option>
                        <option>7 1/2 Baths</option>
                        <option>8 Baths</option>
                        <option>8 1/2 Baths</option>
                        <option>9 Baths</option>
                        <option>9 1/2 Baths</option>
                        <option>10 Baths</option>
                        <option>10 1/2 Baths</option>
                        <option>11 Baths</option>
                        <option>11 1/2 Baths</option>
                        <option>12 Baths</option>
                      </SelectField>
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-slate-700">
                        Dining Area
                      </label>
                      <InputField
                        className={fieldClass(inputBase, "dining_area")}
                        name="dining_area"
                        type="text"
                        value={propertyData["dining_area"] || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap justify-center gap-6">
                    <div className="flex flex-col">
                      <label>
                        Acreage <span className="text-red-600 ml-1">*</span>
                      </label>
                      <InputField
                        className={fieldClass(
                          `${inputBase} ${
                            autoLocked ? "opacity-70 cursor-not-allowed" : ""
                          }`,
                          "acreage"
                        )}
                        name="acreage"
                        type="text"
                        inputMode="decimal"
                        pattern="^(?:\\d{1,6}(?:\\.\\d{0,4})?|\\.\\d{1,4})$"
                        value={propertyData["acreage"] || ""}
                        onChange={handleChange}
                        onBlur={() => handleBlurField("acreage")}
                        readOnly={autoLocked}
                        required
                      />
                      {touched.has("acreage") && errors.acreage && (
                        <span className="text-sm text-red-600">
                          {errors.acreage}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <label>
                        Living SqFt <span className="text-red-600 ml-1">*</span>
                      </label>
                      <InputField
                        className={fieldClass(
                          `${inputBase} ${
                            autoLocked ? "opacity-70 cursor-not-allowed" : ""
                          }`,
                          "living_sqft"
                        )}
                        name="living_sqft"
                        type="text"
                        value={propertyData["living_sqft"] || ""}
                        onChange={handleChange}
                        onBlur={() => handleBlurField("living_sqft")}
                        readOnly={autoLocked}
                        required
                      />
                      {touched.has("living_sqft") && errors.living_sqft && (
                        <span className="text-sm text-red-600">
                          {errors.living_sqft}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap justify-center gap-6">
                    <div className="flex flex-col">
                      <label>
                        Living Areas{" "}
                        <span className="text-red-600 ml-1">*</span>
                      </label>
                      <InputField
                        className={fieldClass(inputBase, "living_areas")}
                        name="living_areas"
                        type="text"
                        value={propertyData["living_areas"] || ""}
                        onChange={handleChange}
                        onBlur={() => handleBlurField("living_areas")}
                        required
                      />
                      {touched.has("living_areas") && errors.living_areas && (
                        <span className="text-sm text-red-600">
                          {errors.living_areas}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col">
                      <label>
                        Est Year Built{" "}
                        <span className="text-red-600 ml-1">*</span>
                      </label>
                      <InputField
                        className={fieldClass(
                          `${inputBase} ${
                            autoLocked ? "opacity-70 cursor-not-allowed" : ""
                          }`,
                          "est_year_built"
                        )}
                        name="est_year_built"
                        type="text"
                        value={propertyData["est_year_built"] || ""}
                        onChange={handleChange}
                        onBlur={() => handleBlurField("est_year_built")}
                        readOnly={autoLocked}
                        required
                      />
                      {touched.has("est_year_built") &&
                        errors.est_year_built && (
                          <span className="text-sm text-red-600">
                            {errors.est_year_built}
                          </span>
                        )}
                    </div>
                  </div>
                  <div className="flex flex-col pt-2">
                    <label>Legal Description</label>
                    <TextareaField
                      className={`${inputBase} resize-y ${
                        autoLocked ? "opacity-70 cursor-not-allowed" : ""
                      }`}
                      name="legal_description"
                      value={propertyData["legal_description"] || ""}
                      onChange={handleChange}
                      readOnly={autoLocked}
                      label="Legal Description"
                    />
                  </div>
                </div>
              ) : (
                ""
              )}
              {propertyData["for"] === "Sale" &&
              (propertyData["type"] === "Commercial" ||
                propertyData["type"] === "Residential") ? (
                <>
                  <div className="flex gap-2 justify-center mt-4">
                    <input
                      className="bg-slate-100"
                      name="vacant_land"
                      type="checkbox"
                      value={propertyData["vacant_land"] || ""}
                      onChange={handleChangeCheck}
                    />
                    <label className="flex gap-2 items-center text-sm font-medium text-slate-700">
                      This is vacant land
                    </label>
                  </div>
                  {propertyData["vacant_land"] === "Yes" ? (
                    <>
                      <div className="flex flex-wrap justify-center gap-6">
                        <div className="flex flex-col">
                          <label>Acreage</label>
                          <InputField
                            className={fieldClass(inputBase, "acreage")}
                            name="acreage"
                            type="text"
                            inputMode="decimal"
                            pattern="^(?:\\d{1,6}(?:\\.\\d{0,4})?|\\.\\d{1,4})$"
                            value={propertyData["acreage"] || ""}
                            onChange={handleChange}
                            onBlur={() => handleBlurField("acreage")}
                            required
                          />
                          {touched.has("acreage") && errors.acreage && (
                            <span className="text-sm text-red-600">
                              {errors.acreage}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <label>Vacant Land Type</label>
                          <SelectField
                            className={fieldClass(
                              `${inputBase} p-[2px] w-[240px]`,
                              "vacant_land_type"
                            )}
                            name="vacant_land_type"
                            value={propertyData["vacant_land_type"] || ""}
                            onChange={handleChange}
                            required
                          >
                            {VACANT_TYPES.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                            {propertyData["type"] === "Commercial" && (
                              <option>Commercial</option>
                            )}
                          </SelectField>
                        </div>
                      </div>
                      <div className="flex flex-col pt-2">
                        <label>Legal Description</label>
                        <TextareaField
                          className={`bg-slate-100 px-4 py-3 resize-y`}
                          name="legal_description"
                          value={propertyData["legal_description"] || ""}
                          onChange={handleChange}
                          label="Legal Description"
                        />
                      </div>
                    </>
                  ) : (
                    ""
                  )}
                </>
              ) : (
                ""
              )}
            </AccordionItemPanel>
          </AccordionItem>
        </Accordion>
      </div>

      {/* Prefill Modal */}
      {showPrefillModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => {
              if (prefillStatus === "error") {
                setShowPrefillModal(false);
                setPrefillStatus("idle");
              }
            }}
          ></div>
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl p-6 z-50 min-w-[300px] text-center">
            {prefillStatus === "loading" && (
              <>
                <div className="animate-spin text-4xl mb-4">⚙️</div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Prefilling Information...
                </h3>
                <p className="text-sm text-gray-600 mt-2">
                  Please wait while we load the property data.
                </p>
              </>
            )}
            {prefillStatus === "success" && (
              <>
                <div className="text-4xl mb-4">✅</div>
                <h3 className="text-lg font-semibold text-green-700">
                  Prefill Complete!
                </h3>
                <p className="text-sm text-gray-600 mt-2">
                  Property information has been loaded successfully.
                </p>
              </>
            )}
            {prefillStatus === "error" && (
              <>
                <div className="text-4xl mb-4">❌</div>
                <h3 className="text-lg font-semibold text-red-700">
                  Prefill Failed
                </h3>
                <p className="text-sm text-gray-600 mt-2">
                  {prefillError ||
                    "Unable to load property data. Please try another listing."}
                </p>
                <button
                  onClick={() => {
                    setShowPrefillModal(false);
                    setPrefillStatus("idle");
                  }}
                  className="mt-4 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium"
                >
                  Close
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Property;
